// The study's board: the page around it, and what it asks its board session to do.
//
// The board itself — Chessground, the dests, the syncing, the replaying — is a session now
// (services/boardSession.ts), the same one /lines uses. What is left here belongs to this page: the
// move list it paints, the FEN field it fills, the arrows it draws, and the handle you resize it by.

import { store, game, DEFAULTS } from "../state/index.ts";
import { live } from "../state/live.ts";
import { K } from "../state/keys.ts";
import { boardSession, BRUSH } from "../services/boardSession.ts";
import { turnColor } from "../domain/boardPosition.ts";
import { clamp } from "./utils.js";

/** The name of the board of the study. The only one on this page, and not the only one in the app. */
const STUDY = "study";

// DOM elements
let boardContainer = null;
let boardResizeHandle = null;

// Callbacks
let onUserMove = null;
let onEvaluatePosition = null;
let onCheckRepertoire = null;
let onLoadBook = null;
let onBeforeUserMove = null;
let onAfterUserMove = null;

// Board resize state
const BOARD_SIZE_KEY = "sparring.chess.BoardSize";
const MIN_BOARD_SIZE = 280;
const MAX_BOARD_SIZE = 600;
const DEFAULT_BOARD_SIZE = 400;

/**
 * The session this page plays on. It shares the game the legacy modules still import by name, so
 * the board and everything reading `game` are looking at the same position.
 */
function board() {
    return boardSession(STUDY, {
        chess: game,
        orientation: DEFAULTS.boardSide,
        onUserMove: handleUserMove,
    });
}

export function initBoard(elements, callbacks = {}) {
    board();
    boardContainer = elements.boardContainer;
    boardResizeHandle = elements.boardResizeHandle;
    onUserMove = callbacks.onUserMove;
    onEvaluatePosition = callbacks.onEvaluatePosition;
    onCheckRepertoire = callbacks.onCheckRepertoire;
    onLoadBook = callbacks.onLoadBook;
    onBeforeUserMove = callbacks.onBeforeUserMove;
    onAfterUserMove = callbacks.onAfterUserMove;

    // Initialize board resize functionality
    initBoardResize();
}

/**
 * Puts the board on the element the Board component owns. Called once, from that component's layout
 * effect, so it exists before the rest of the boot touches it. `live.ground` stays pointed at it
 * while the legacy modules still reach for the board by that name.
 */
export function attachBoard(element) {
    const session = board();
    session.attach(element);
    live.ground = session.api;
}

export function detachBoard() {
    board().detach();
    live.ground = null;
}

export function getTurnColor() {
    return turnColor(game);
}

export function setMovableSide(side) {
    board().setMovableSide(side);
}

export function syncBoard(lastMove) {
    board().sync(lastMove);
    // A hint is about a position; the position has changed
    if (store.get('repertoire.hintMode')) {
        store.set('repertoire.hintMode', null);
    }
    if (onEvaluatePosition) onEvaluatePosition();
    if (onCheckRepertoire) onCheckRepertoire();
}

/**
 * Force Chessground to recalculate board dimensions.
 * Call after any layout change that affects the board container size.
 */
export function redrawBoard() {
    board().redraw();
}

/**
 * @param sanHistory  full move list to replay from. Needed to move FORWARD: once the board has gone
 *                    back, the game itself no longer knows the moves that came after.
 */
export function jumpToPly(targetPly, sanHistory = null) {
    board().jumpToPly(targetPly, sanHistory || undefined);
    if (onLoadBook) {
        onLoadBook();
    }
}

/** A move the user made by dragging a piece. The session has already played it. */
function handleUserMove(move) {
    if (onBeforeUserMove) onBeforeUserMove();
    if (store.get('repertoire.hintMode')) store.set('repertoire.hintMode', null);
    if (onEvaluatePosition) onEvaluatePosition();
    if (onCheckRepertoire) onCheckRepertoire();
    if (onAfterUserMove) {
        onAfterUserMove({ san: move.san, uci: move.uci, fenBefore: move.fenBefore, fenAfter: move.fenAfter });
    }
    if (onUserMove) {
        onUserMove({ from: move.from, to: move.to, isGameOver: move.gameOver });
    }
}

/** A move made for the player: a book reply, a line being replayed. */
export function applyMove(move) {
    if (store.get('config.debugOn')) {
        console.log("Applying book move", move);
    }
    return !!move && board().play(move);
}

export function loadFen(fen) {
    const loaded = board().loadFen(fen);
    if (!loaded) return false;
    store.set('book.latestMoves', []);
    return true;
}

/**
 * Puts a line on the board: its starting position (null = the initial one) plus the UCI moves that
 * lead from there to the position to practice.
 */
export function loadLine(fen, uciMoves) {
    board().loadLine(fen, uciMoves);
    store.set('book.latestMoves', []);
}

export function updateBookOptions() {
    const shapes = [];
    const brushes = {};

    const latestRepertoireMoves = store.get('repertoire.latestMoves');

    // The engine's lines: the "Lines" switch of the study's engine row (components/study/LivePanel.tsx)
    const showEngineLines = store.get('engine.showLines');
    if (showEngineLines) {
        const pvLines = store.get('engine.pvLines') || {};
        const sortedKeys = Object.keys(pvLines).map(Number).sort((a, b) => a - b);
        sortedKeys.forEach((pvNum, idx) => {
            const info = pvLines[pvNum];
            if (!info?.pv) return;
            const firstMove = info.pv.split(/\s+/)[0];
            if (!firstMove || firstMove.length < 4) return;
            const orig = firstMove.slice(0, 2);
            const dest = firstMove.slice(2, 4);
            const opacity = clamp(0.3, 0.9, 1 - (idx * 0.2));
            const key = `engine-${pvNum}`;
            brushes[key] = { key, color: BRUSH.engine, opacity, lineWidth: 10 };
            shapes.push({ orig, dest, brush: key });
        });
    }

    // Practice hint: only the piece to move, the destination is for the user to find
    const practiceHint = store.get('practice.hintUci');
    if (practiceHint) {
        brushes['practice-hint'] = { key: 'practice-hint', color: BRUSH.hint, opacity: 0.85, lineWidth: 12 };
        shapes.push({ orig: practiceHint.slice(0, 2), brush: 'practice-hint' });
    }

    // Repertoire hint overlay (from quick status "Show Hint" / "Show Move")
    const hintMode = store.get('repertoire.hintMode');
    if (hintMode && latestRepertoireMoves.length) {
        const firstPv = latestRepertoireMoves[0];
        const firstMove = firstPv.split(/\s+/)[0];
        if (firstMove && firstMove.length >= 4) {
            const orig = firstMove.slice(0, 2);
            const dest = firstMove.slice(2, 4);
            if (hintMode === 'hint') {
                // Circle on the origin square only
                brushes['rep-hint'] = { key: 'rep-hint', color: BRUSH.hint, opacity: 0.8, lineWidth: 12 };
                shapes.push({ orig, brush: 'rep-hint' });
            } else if (hintMode === 'move') {
                // Full arrow showing the move
                brushes['rep-show'] = { key: 'rep-show', color: BRUSH.hint, opacity: 0.85, lineWidth: 12 };
                shapes.push({ orig, dest, brush: 'rep-show' });
            }
        }

        // PGN embedded annotations ([%csl] / [%cal]) for the first suggested move
        const repStatus = store.get('repertoire.status');
        const annSquares = repStatus?.nextSquares?.[0] || [];
        const annArrows = repStatus?.nextArrows?.[0] || [];
        annSquares.forEach((ann, i) => {
            const color = pgnColorToHex(ann[0]);
            const square = ann.slice(1);
            if (!color || square.length < 2) return;
            const key = `rep-ann-sq-${i}`;
            brushes[key] = { key, color, opacity: 0.75, lineWidth: 8 };
            shapes.push({ orig: square, brush: key });
        });
        annArrows.forEach((ann, i) => {
            const color = pgnColorToHex(ann[0]);
            const a = ann.slice(1, 3);
            const b = ann.slice(3, 5);
            if (!color || a.length < 2 || b.length < 2) return;
            const key = `rep-ann-ar-${i}`;
            brushes[key] = { key, color, opacity: 0.75, lineWidth: 10 };
            shapes.push({ orig: a, dest: b, brush: key });
        });
    }

    board().setShapes(shapes, brushes);
}

const PGN_COLOR_MAP = { G: BRUSH.hint, R: BRUSH.engine, Y: BRUSH.warning, B: BRUSH.repertoire };
function pgnColorToHex(c) {
    return PGN_COLOR_MAP[c] || null;
}

// Board resize functionality
function initBoardResize() {
    if (!boardContainer || !boardResizeHandle) return;

    // Restore saved board size (constrained to current viewport)
    const savedSize = localStorage.getItem(BOARD_SIZE_KEY);
    if (savedSize) {
        const maxSize = getMaxBoardSize();
        const size = clamp(MIN_BOARD_SIZE, maxSize, parseInt(savedSize, 10));
        setBoardSize(size);
    }

    let isResizing = false;
    let startX = 0;
    let startY = 0;
    let startWidth = 0;

    function onResizeStart(e) {
        e.preventDefault();
        isResizing = true;
        boardContainer.classList.add("resizing");

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        startX = clientX;
        startY = clientY;
        startWidth = boardContainer.offsetWidth;

        document.addEventListener("mousemove", onResizeMove);
        document.addEventListener("mouseup", onResizeEnd);
        document.addEventListener("touchmove", onResizeMove, { passive: false });
        document.addEventListener("touchend", onResizeEnd);
    }

    function onResizeMove(e) {
        if (!isResizing) return;
        e.preventDefault();

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        // Use the larger of X or Y delta for diagonal resize
        const deltaX = clientX - startX;
        const deltaY = clientY - startY;
        const delta = Math.max(deltaX, deltaY);

        const maxSize = getMaxBoardSize();
        const newWidth = clamp(MIN_BOARD_SIZE, maxSize, startWidth + delta);
        setBoardSize(newWidth);
    }

    function onResizeEnd() {
        if (!isResizing) return;
        isResizing = false;
        boardContainer.classList.remove("resizing");

        // Save the new size
        const currentSize = boardContainer.offsetWidth;
        localStorage.setItem(BOARD_SIZE_KEY, currentSize.toString());

        document.removeEventListener("mousemove", onResizeMove);
        document.removeEventListener("mouseup", onResizeEnd);
        document.removeEventListener("touchmove", onResizeMove);
        document.removeEventListener("touchend", onResizeEnd);

        // Trigger Chessground redraw
        const ground = live.ground;
        if (ground) {
            ground.redrawAll();
        }
    }

    boardResizeHandle.addEventListener("mousedown", onResizeStart);
    boardResizeHandle.addEventListener("touchstart", onResizeStart, { passive: false });
}

function getMaxBoardSize() {
    // On mobile/narrow screens, limit to viewport width minus padding
    const padding = 32; // var(--space-4) * 2 = 16px * 2
    const viewportMax = window.innerWidth - padding;
    return Math.min(MAX_BOARD_SIZE, viewportMax);
}

function setBoardSize(size) {
    if (!boardContainer) return;
    const maxSize = getMaxBoardSize();
    const constrainedSize = Math.min(size, maxSize);
    boardContainer.style.width = `${constrainedSize}px`;
}
